/** Turn the server's flat work-code list into the DISPLAYED rows, applying the
 *  staged code edits and grouping by EFFECTIVE (code, name) — so a merge (which
 *  recodes several sources onto one target) collapses into a single row, while
 *  reused codes with different names stay separate. Pure + testable.
 */
import type { ExistingWorkCode } from "../../../../models";
import type { CodeEdit } from "../state";

export interface CodeSource {
  code: string;
  prefix: string;
}

export interface CodeGroup {
  /** STABLE id derived from the source codes (sorted, joined) — used as the row
   *  identity / React key / selection key. It survives rename/recode (the
   *  sources don't change), so inline edits never re-mount or orphan the row;
   *  it only changes on a merge (sources combine), where selection clears. */
  key: string;
  code: string;
  name: string;
  /** Effective color (hex) or "" — the renderer falls back to colorForPrefix. */
  color: string;
  /** The snapshot entries collapsed into this row (drives fetch + merge). */
  sources: CodeSource[];
  activityCount: number;
  /** Non-completed carriers across the group — `> 0` ⇒ "on the board". */
  activeCount: number;
  sections: string[];
}

export function buildCodeGroups(
  codes: ExistingWorkCode[],
  codeEdits: Record<string, CodeEdit>,
): CodeGroup[] {
  // Group by EFFECTIVE (code,name) so a merge collapses sources together…
  const byEff = new Map<string, CodeGroup>();
  const order: string[] = [];

  for (const c of codes) {
    const srcKey = `${c.code}|${c.prefix}`;
    const e = codeEdits[srcKey];
    const code = e?.newCode ?? c.code;
    const name = e?.name ?? c.prefix;
    const effKey = `${code}|${name}`;

    let g = byEff.get(effKey);
    if (!g) {
      g = {
        key: "", code, name, color: "",
        sources: [], activityCount: 0, activeCount: 0, sections: [],
      };
      byEff.set(effKey, g);
      order.push(effKey);
    }
    g.sources.push({ code: c.code, prefix: c.prefix });
    g.activityCount += c.activity_count;
    g.activeCount += c.active_count ?? 0;
    for (const s of c.sections) if (g.sections.indexOf(s) === -1) g.sections.push(s);
    // An edited color wins; otherwise fill from a source only if not already set
    // (don't let a later, uncolored source clobber an earlier edited color).
    if (e?.color !== undefined) g.color = e.color;
    else if (!g.color && c.color) g.color = c.color;
  }

  // …but the EXPOSED key is the stable source id, not the mutable effective one.
  const groups = order.map((k) => byEff.get(k) as CodeGroup);
  for (const g of groups) {
    g.key = g.sources
      .map((s) => `${s.code}|${s.prefix}`)
      .slice()
      .sort()
      .join("¦");
  }
  return groups;
}
